import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Search, Loader2, X, Check } from "lucide-react";
import {
  searchLocations,
  getBrowserLocation,
  reverseGeocodeCoords,
} from "../api/geocodingApi";

/**
 * Reusable location picker. Used in settings, browse page, and create-listing.
 *
 * Two ways for a user to set their location:
 *   1. Click "Use my current location" — browser geolocation API
 *   2. Type into the search box — autocomplete suggestions from the backend
 *
 * Props:
 *   value          — current selected location { coordinates, label } or null
 *   onChange       — called with { coordinates: [lng, lat], label, precision }
 *                    or null when cleared
 *   countryCode    — ISO 2-letter, biases search results (default "ZA")
 *   disabled       — disables all inputs
 *   error          — error message to display below the picker
 *   placeholder    — custom placeholder for the search input
 *   showClearButton — show an X to clear the selection (default true)
 *   compact        — compact styling for use in filter panels (default false)
 */
export const LocationPicker = ({
  value,
  onChange,
  countryCode = "ZA",
  disabled = false,
  error,
  placeholder = "Search for a suburb or city...",
  showClearButton = true,
  compact = false,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserError, setBrowserError] = useState("");

  const wrapperRef = useRef(null);
  // Keep the latest abort controller so we can cancel in-flight searches when the user keeps typing
  const abortRef = useRef(null);
  // Debounce timer for the search-as-you-type behavior
  const debounceRef = useRef(null);

  /**
   * Close the suggestions dropdown when clicking outside the component.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Debounced search — fires 300ms after the user stops typing.
   * Cancels any in-flight request when a new keystroke comes in.
   */
  const runSearch = useCallback(
    async (query) => {
      // Cancel any previous in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setSearchLoading(true);
      try {
        const results = await searchLocations(query, countryCode);
        // Don't update state if this request was superseded
        if (!controller.signal.aborted) {
          setSuggestions(results);
          setShowSuggestions(true);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Location search error:", err);
          setSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearchLoading(false);
        }
      }
    },
    [countryCode]
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchInput(newValue);
    setBrowserError(""); // Clear any browser geolocation error when user types

    // Clear pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Don't search if too short
    if (newValue.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSearchLoading(false);
      return;
    }

    // Debounce — wait 300ms after typing stops
    debounceRef.current = setTimeout(() => {
      runSearch(newValue);
    }, 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange({
      coordinates: suggestion.coordinates,
      label: suggestion.label,
      addressParts: suggestion.addressParts,
      precision: "geocoded",
    });
    setSearchInput("");
    setSuggestions([]);
    setShowSuggestions(false);
    setBrowserError("");
  };

  const handleUseCurrentLocation = async () => {
    setBrowserError("");
    setBrowserLoading(true);
    try {
      const { lng, lat } = await getBrowserLocation();

      // We have coordinates — now fetch a label so the picker can display
      // a real location name instead of just "Location selected".
      const { label, addressParts } = await reverseGeocodeCoords(lng, lat);

      onChange({
        coordinates: [lng, lat],
        label,
        addressParts,
        precision: "gps",
      });
      setSearchInput("");
      setSuggestions([]);
      setShowSuggestions(false);
    } catch (err) {
      setBrowserError(err.message || "Could not get your location");
    } finally {
      setBrowserLoading(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setSearchInput("");
    setSuggestions([]);
    setShowSuggestions(false);
    setBrowserError("");
  };

  // What's currently displayed as "the selected location"
  const hasValue = value && value.label;

  return (
    <div ref={wrapperRef} className="w-full">
      {/* Selected location display (shown when a value is set) */}
      {hasValue && (
        <div
          className={`flex items-center justify-between gap-2 mb-2 px-3 py-2 bg-sage-50 border border-sage-200 rounded-lg ${
            compact ? "text-sm" : ""
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Check className="w-4 h-4 text-sage-600 flex-shrink-0" />
            <span className="text-sage-900 truncate">{value.label}</span>
          </div>
          {showClearButton && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-sage-600 hover:text-sage-900 flex-shrink-0"
              aria-label="Clear location"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchInput}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          placeholder={hasValue ? "Change location..." : placeholder}
          disabled={disabled}
          className={`w-full pl-10 pr-10 ${
            compact ? "py-2 text-sm" : "py-2.5"
          } border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-sage-500 ${
            error ? "border-red-500" : "border-gray-300"
          } ${disabled ? "bg-gray-100 cursor-not-allowed opacity-60" : "bg-white"}`}
        />
        {searchLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto">
            {suggestions.map((suggestion, idx) => (
              <button
                key={`${suggestion.coordinates.join(",")}-${idx}`}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full text-left px-4 py-2.5 hover:bg-sage-50 border-b border-gray-100 last:border-b-0 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-sage-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {suggestion.label}
                    </div>
                    {suggestion.displayName &&
                      suggestion.displayName !== suggestion.label && (
                        <div className="text-xs text-gray-500 truncate">
                          {suggestion.displayName}
                        </div>
                      )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* "No results" hint */}
        {showSuggestions &&
          !searchLoading &&
          suggestions.length === 0 &&
          searchInput.trim().length >= 2 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-sm text-gray-500 text-center">
              No matches found. Try a broader search.
            </div>
          )}
      </div>

      {/* Use current location button */}
      <button
        type="button"
        onClick={handleUseCurrentLocation}
        disabled={disabled || browserLoading}
        className={`mt-2 w-full flex items-center justify-center gap-2 ${
          compact ? "py-1.5 text-sm" : "py-2"
        } px-4 border border-sage-200 text-sage-700 rounded-lg hover:bg-sage-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {browserLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Getting your location...</span>
          </>
        ) : (
          <>
            <MapPin className="w-4 h-4" />
            <span>Use my current location</span>
          </>
        )}
      </button>

      {/* Error messages */}
      {browserError && (
        <p className="text-red-500 text-sm mt-1">{browserError}</p>
      )}
      {error && !browserError && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
    </div>
  );
};

export default LocationPicker;